import React, { useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { useLocation, useHistory } from 'react-router-dom';
import css from './SearchPage.module.css';
import { capitalizeFirstLetter } from '../../util/genericHelpers';
import { Icons } from '../../components';
import { PARAM_KEYS, UserType } from '../../util/enums';

const CATEGORIES = ['talent', 'brand', 'school'];

const SearchBar = ({ onSearch, hideCategories = false, placeholder, activeCategory = null }) => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const urlCategory = searchParams.get(PARAM_KEYS.ListingCategory);
  const getDefaultCategory = () => {
    const validOptions = [
      activeCategory,
      ...(CATEGORIES.includes(urlCategory) ? [urlCategory] : []),
    ];
    return validOptions.find(Boolean) || UserType.TALENT;
  };

  const defaultCategory = getDefaultCategory();
  const [searchInput, setSearchInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);

  // Update URL when category changes
  const updateUrlWithCategory = category => {
    const params = new URLSearchParams(location.search);
    if (category) {
      params.set(PARAM_KEYS.ListingCategory, category);
    } else {
      params.delete(PARAM_KEYS.ListingCategory);
    }
    const newSearch = params.toString();
    history.push({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : '',
    });
  };

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((input, category) => {
      if (input.length < 2) return;
      if (onSearch) {
        onSearch(input, category);
        if (input.length >= 2) {
          setSearchInput('');
        }
      }
    }, 500),
    [onSearch]
  );

  const handleInputChange = useCallback(
    e => {
      const value = e.target.value;
      setSearchInput(value);
      debouncedSearch(value, selectedCategory || defaultCategory);
    },
    [debouncedSearch, selectedCategory]
  );

  const handleCategoryChange = useCallback(
    e => {
      const value = e.target.value;
      setSelectedCategory(value);
      updateUrlWithCategory(value); // Update URL immediately on category change
      if (searchInput.length >= 2) {
        debouncedSearch(searchInput, value);
      }
    },
    [debouncedSearch, searchInput, updateUrlWithCategory]
  );

  return (
    <div className={css.searchBarContainer}>
      <div className={css.searchKeywordSec}>
        <Icons name="search" className={css.searchIcon} />
        <input
          type="text"
          placeholder={
            placeholder ||
            (selectedCategory === UserType.TALENT
              ? 'Search by first or last name (min 2 letters)'
              : 'Search...')
          }
          value={searchInput}
          onChange={handleInputChange}
          className={css.searchInput}
          aria-label="Search by keyword"
        />
      </div>
      {!hideCategories && (
        <div className={css.chooseCategorySec}>
          <select
            value={selectedCategory}
            onChange={handleCategoryChange}
            className={css.categorySelect}
            aria-label="Select search category"
          >
            {CATEGORIES.map(category => (
              <option key={category} value={category}>
                {capitalizeFirstLetter(category)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
